import { FunctionComponent, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { ReactComponent as Email } from "@assets/icons/email.svg";
import { ReactComponent as Linkedin } from "@assets/icons/linkedin.svg";

import { Modal } from "@/components";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { InputField } from "@/components/InputField/InputField";
import { getAllErrors, renderErrorMessages } from "@/utils";

import {
	SeatAddonType,
	useSetBonusSeatsMutation,
} from "@/pages/Private/redux/subscription/subscription.api";
import { useSlotsAvailableQuery } from "@/pages/Private/redux/unipile/unipile.api";

import { CompanySeatSummary, SeatKindSummary } from "../schema/companies";
import {
	BonusSeatsForm,
	changedBonusGrants,
	createBonusSeatsSchema,
	MAX_BONUS_SEATS,
	parseBonusInput,
	slotsToSeatSummary,
	wouldExceedCapacity,
} from "../utils/seatSummary";
import { BonusBadge } from "./LicencesCell";

interface ManageLicencesModalProps {
	companyId: number;
	companyName: string;
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	/** Called after every changed grant has been saved — the host shows the toast. */
	onSaved?: () => void;
}

const KIND_ICON: Record<SeatAddonType, typeof Linkedin> = { linkedin: Linkedin, email: Email };

const SeatKindSection: FunctionComponent<{
	type: SeatAddonType;
	kind: SeatKindSummary;
	value: number;
	errorMessage?: string;
	onChange: (raw: string) => void;
}> = ({ type, kind, value, errorMessage, onChange }) => {
	const { t } = useTranslation();
	const tl = (key: string, options?: Record<string, unknown>) =>
		t(`companies.licences.${key}`, options);

	return (
		<section
			className="flex flex-col gap-2 rounded-xl border border-border-light p-4"
			data-test={`licences-${type}`}
		>
			<div className="flex items-center gap-2 text-sm font-semibold">
				<SvgIcon className="h-[18px] w-[18px]" svgIcon={KIND_ICON[type]} />
				{tl(type)}
				<BonusBadge amount={kind.bonus} />
			</div>
			<div className="text-sm text-text-meta">
				{tl("used", { used: kind.used, total: kind.total })}
			</div>
			<div className="text-xs text-text-meta">
				{tl("breakdown", { included: kind.included, paid: kind.paid, bonus: kind.bonus })}
			</div>
			<InputField
				error={!!errorMessage}
				handleChange={(e) => onChange(e.target.value)}
				label={tl("bonusSeats")}
				max={MAX_BONUS_SEATS}
				min={0}
				name={`${type}-bonus`}
				testId={`${type}-bonus-input`}
				type="number"
				value={Number.isNaN(value) ? "" : value}
			/>
			{errorMessage && (
				<p className="text-xs text-status-error" data-test={`${type}-bonus-error`}>
					{errorMessage}
				</p>
			)}
			{wouldExceedCapacity(kind, value) && (
				<div
					className="rounded-md bg-status-warning-light p-2 text-xs text-text"
					data-test={`${type}-in-use-warning`}
				>
					{tl("inUseWarning", { used: kind.used })}
				</div>
			)}
		</section>
	);
};

/**
 * Staff-only editor for a company's bonus LinkedIn / email seats (PREC-4063).
 *
 * Reads the exact breakdown from `GET unipile/slots-available/:companyId` (the same calculation
 * the customer's Billing page uses) and sends one `PATCH stripe-subscription/bonus-seats` per
 * seat kind whose bonus changed. An unchanged form closes without a request.
 */
export const ManageLicencesModal: FunctionComponent<ManageLicencesModalProps> = ({
	companyId,
	companyName,
	isOpen,
	setIsOpen,
	onSaved,
}) => {
	const { t } = useTranslation();
	const tl = (key: string, options?: Record<string, unknown>) =>
		t(`companies.licences.${key}`, options);

	const { data: slots, isLoading: isLoadingSlots } = useSlotsAvailableQuery(companyId);
	const [setBonusSeats, { isLoading: isSaving, error }] = useSetBonusSeatsMutation();

	const summary: CompanySeatSummary | null = useMemo(
		() => (slots ? slotsToSeatSummary(slots) : null),
		[slots]
	);

	const schema = useMemo(
		() => createBonusSeatsSchema(tl("invalid", { max: MAX_BONUS_SEATS })),
		[t]
	);

	const {
		handleSubmit,
		setValue,
		reset,
		watch,
		formState: { errors, isValid },
	} = useForm<BonusSeatsForm>({
		defaultValues: { linkedin: 0, email: 0 },
		mode: "onChange",
		resolver: zodResolver(schema),
	});

	// The current bonus arrives with the slots query; seed the form once it does.
	useEffect(() => {
		if (summary) {
			reset({ linkedin: summary.linkedin.bonus, email: summary.email.bonus });
		}
	}, [summary, reset]);

	const values = watch();

	const onSubmit = async (next: BonusSeatsForm) => {
		if (!summary) {
			return;
		}

		const grants = changedBonusGrants(
			{ linkedin: summary.linkedin.bonus, email: summary.email.bonus },
			next
		);

		try {
			for (const grant of grants) {
				await setBonusSeats({ companyId, ...grant }).unwrap();
			}

			if (grants.length > 0) {
				onSaved?.();
			}

			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const setBonus = (type: SeatAddonType) => (raw: string) =>
		setValue(type, parseBonusInput(raw), { shouldValidate: true, shouldDirty: true });

	return (
		<Modal
			disableSave={!summary || !isValid || isSaving}
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isSaving}
			isOpened={isOpen}
			submitButtonText={t("basics.confirm")}
			title={tl("title", { name: companyName })}
		>
			<div className="flex flex-col gap-4">
				{isLoadingSlots && !summary && <p className="text-sm text-text-meta">{tl("loading")}</p>}
				{summary && (
					<>
						<SeatKindSection
							errorMessage={errors.linkedin?.message}
							kind={summary.linkedin}
							type="linkedin"
							value={values.linkedin}
							onChange={setBonus("linkedin")}
						/>
						<SeatKindSection
							errorMessage={errors.email?.message}
							kind={summary.email}
							type="email"
							value={values.email}
							onChange={setBonus("email")}
						/>
					</>
				)}
				{/* Field messages render inline above; this is the server's answer only. */}
				{error ? renderErrorMessages(getAllErrors(error, [])) : null}
			</div>
		</Modal>
	);
};
